import type { ResponsibleTeam } from "@/components/AvatarStack";
import type { Person } from "@/lib/person";
import { timeAgo } from "@/lib/time";

export type { Person };

// Shapes mirror TaskSerialization#serialize_item exactly — this file is the
// one place that turns those raw fields into page vocabulary (zones, due
// words, why-phrases). `state`/`kind` are deliberately opaque per-source
// strings on the backend — the maps below are a UI-only projection, not a
// contract.

export interface Task {
  id: string;
  kind: string;
  state: string;
  details: string;
  description: string;
  url: string | null;
  due_on: string;
  /** The viewer's own due date while the task waits on them (TaskTurn), else null. */
  my_due_on: string | null;
  state_entered_at: string;
  last_activity_on: string | null;
  completed_at: string | null;
  resolution: string | null;
  org_node_id: string;
  org_node_name: string;
  market_name: string | null;
  sent_back_by: string | null;
  has_review?: boolean;
  reviewers: { id: string; name: string; initials: string; avatar_url: string | null }[];
  responsible: ResponsibleTeam | null;
  claimed_by: { id: string; name: string; initials: string; avatar_url: string | null } | null;
  snoozed_until: string | null;
  snooze_reason: string | null;
  snoozed_by_name: string | null;
  snoozed_by_me: boolean;
  can: TaskCan;
}

// One pickable person on the drawer's people picker (TasksController#people_props).
export interface TaskCan {
  claim: boolean;
  assign: boolean;
  nudge: boolean;
  snooze: boolean;
  resume: boolean;
  submit: boolean;
  approve: boolean;
  request_changes: boolean;
  close: boolean;
  delete: boolean;
  edit_pics: boolean;
  edit_reviewers: boolean;
}

// One row of the drawer's activity feed (Tasks::ActivitiesController#show).
// `kind` is an open-ended string — new kinds render via the humanized
// fallback rather than requiring a matching frontend deploy.
export interface ActivityEvent {
  kind: string;
  at: string;
  actor_name: string | null;
  actor_initials: string | null;
  actor_avatar_url: string | null;
  note: string | null;
  detail: string | null;
  /** Send-type events only: how many people the notification fanned out to. */
  recipients?: number;
}

// ---------------------------------------------------------------- due words

function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Short "Aug 16" label for a bare YYYY-MM-DD date — parsed at local midnight
// via the `T00:00:00` anchor idiom, never `new Date(bareYYYYMMDD)`, which
// parses as UTC and can print the wrong day near a timezone boundary.
export function shortDateLabel(dateOnly: string): string {
  return new Date(`${dateOnly}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function dueDays(dueOn: string): number {
  const today = localMidnight(new Date());
  const due = localMidnight(new Date(`${dueOn.slice(0, 10)}T00:00:00`));
  return Math.round((due - today) / 86_400_000);
}

export type DueTone = "overdue" | "today" | "none";

// Time words: "{n} days overdue" · "due today" · "tomorrow" · "Due Jan 1
// (in 4 months)". Red and amber exist only in these words.
// `sub` is the relative part ("(next week)") split out so row layouts can
// stack it under the date.
export function dueWords(dueOn: string): { text: string; sub?: string; tone: DueTone } {
  const days = dueDays(dueOn);
  if (days < 0) return { text: `${-days} day${days === -1 ? "" : "s"} overdue`, tone: "overdue" };
  if (days === 0) return { text: "due today", tone: "today" };
  if (days === 1) return { text: "tomorrow", tone: "none" };
  return { text: `Due ${shortDateLabel(dueOn)}`, sub: dueDistance(dueOn), tone: "none" };
}

// "(next week)" · "(in 4 months)": the feed's timeAgo wording for a due date,
// anchored at local midnight today so the words never drift with the time
// of day. Shared by the row's sub-line and the drawer's due line.
export function dueDistance(dueOn: string): string {
  return `(${timeAgo(`${dueOn.slice(0, 10)}T00:00:00`, localMidnight(new Date()))})`;
}

// Completed rows: weekday inside the last week ("Tue"), short date beyond.
export function completedWords(completedAt: string): string {
  const days = Math.round((localMidnight(new Date()) - localMidnight(new Date(completedAt))) / 86_400_000);
  if (days <= 6) return new Date(completedAt).toLocaleDateString("en-US", { weekday: "short" });
  return new Date(completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------- zones

export type Zone = "attention" | "upcoming" | "paused" | "done";

// Paused = reminders deliberately snoozed until a date. Quiet, never hidden.
export function isPaused(task: Pick<Task, "snoozed_until">): boolean {
  if (!task.snoozed_until) return false;
  return localMidnight(new Date(`${task.snoozed_until}T00:00:00`)) > localMidnight(new Date());
}

// Needs-attention membership is fixed: overdue ∪ due today ∪ sent-back ∪
// awaiting MY review. Nothing else ever promotes.
// The date this viewer works to: their own due date while the task waits on
// them, else the task overall due date.
export function workingDue(task: Pick<Task, "due_on" | "my_due_on">): string {
  return task.my_due_on ?? task.due_on;
}

export function zoneFor(task: Task): Zone {
  if (task.completed_at) return "done";
  if (isPaused(task)) return "paused";
  if (task.state === "changes_requested") return "attention";
  if (task.state === "submitted_for_review") return task.can.approve ? "attention" : "upcoming";
  return dueDays(workingDue(task)) <= 0 ? "attention" : "upcoming";
}

// Needs attention: most-overdue first, then title as a stable tiebreak.
export function attentionOrder(a: Task, b: Task): number {
  const days = dueDays(workingDue(a)) - dueDays(workingDue(b));
  if (days !== 0) return days;
  return a.details.localeCompare(b.details);
}

// ---------------------------------------------------------------- title

// The reviewer's judging turn reads as a review, not a to-do. Everyone else
// (the doer included) keeps the plain title.
export function titleFor(task: Pick<Task, "state" | "details" | "can">): string {
  return task.state === "submitted_for_review" && task.can.approve ? `Review: ${task.details}` : task.details;
}

// ---------------------------------------------------------------- why-phrases

// Second line only when it changes a decision: returned work, or work
// sitting in review.
export type WhyTone = "neutral" | "info" | "amber";

export function whyFor(task: Pick<Task, "state" | "sent_back_by">): { text: string; tone: WhyTone } | null {
  if (task.state === "submitted_for_review") return { text: "Waiting for review", tone: "info" };
  if (task.state === "changes_requested") {
    return { text: task.sent_back_by ? `${task.sent_back_by} requested changes` : "Changes requested", tone: "amber" };
  }
  return null;
}

// ---------------------------------------------------------------- deep link

// Accepts either a full URL (window.location.href) or a bare path-with-query
// (an Inertia page.url, e.g. "/tasks?task=abc").
export function taskIdFromUrl(url: string): string | null {
  return new URL(url, "http://placeholder").searchParams.get("task") || null;
}
